import {
  PAYROLL_SETTINGS_MENU,
  PAYROLL_SETTINGS_OVERVIEW,
  settingsItemHref,
} from "pages/PayRoll/Configuration/payrollSettingsMenu";
import { getCachedPayInputConfig } from "pages/PayRoll/Configuration/PayInputConfiguration/payInputConfigApi";
import {
  CONFIG_GATED_FLYOUT_ITEMS,
  isSectionRowAccessible,
  isSubmenuAccessible,
} from "components/Sidebar/sidebarAccess";

// Everything the top-bar search can open: pages, Payroll settings and quick
// actions. Pages come straight from the sidebar's routes, so a new sidebar
// page is searchable without changes here. Only the extra search words and
// the quick actions are listed by hand.

// Extra words that find a page (spec: "OT" -> Overtime, "ECR" -> PF).
// Keyed by the page's route, as in routes.js.
const PAGE_KEYWORDS = {
  "/admin/payroll/pay-inputs/overtime": ["ot", "overtime hours", "extra hours"],
  "/admin/payroll/pay-inputs/shift-earnings": ["shift allowance", "night shift"],
  "/admin/payroll/pay-inputs/leave": ["leave encashment", "leave earnings"],
  "/admin/payroll/pay-inputs/loans-and-advances": ["loan", "advance", "emi"],
  "/admin/payroll/pay-inputs/reimbursements": ["expense", "claims"],
  "/admin/payroll/pay-inputs/penalties": ["fine", "deduction"],
  "/admin/payroll/pay-inputs/salary-holds": ["hold salary"],
  "/admin/payroll/statutory/pf": ["epf", "ecr", "provident fund", "uan"],
  "/admin/payroll/statutory/esi": ["esic", "employee state insurance"],
  "/admin/payroll/statutory/pt": ["professional tax"],
  "/admin/payroll/statutory/lwf": ["labour welfare fund", "labor welfare"],
  "/admin/payroll/salary/salary-components": ["earnings", "deductions", "pay heads"],
  "/admin/payroll/salary/salary-templates": ["salary structure", "ctc"],
  "/admin/payroll/salary/salary-revision": ["increment", "hike", "appraisal"],
  "/admin/payroll/pay-processing/payroll-cycle-run": ["payrun", "pay run", "run payroll"],
  "/admin/payroll/disbursement/salary-payments": ["pay salary", "payment batch"],
  "/admin/payroll/disbursement/bank-file-export": ["bank file", "neft"],
  "/admin/myzone/payslip": ["salary slip", "pay slip"],
  "/admin/myzone/myworkinghours": ["my attendance", "working hours"],
  "/admin/teamzone/approve": ["approve", "pending requests"],
  "/admin/teamzone/plan-shift": ["roster", "shift plan"],
  "/admin/hrzone/employees": ["staff", "people", "onboard"],
  "/admin/hrzone/timeoff": ["leave settings", "holidays"],
  "/admin/administration/user": ["users", "login"],
  "/admin/administration/role/usergroup": ["roles", "permissions", "user group"],
};

// Extra words that find a Payroll setting, keyed by the setting's key.
const SETTING_KEYWORDS = {
  overtime: ["ot", "overtime rate rules"],
  pf: ["epf", "ecr", "provident fund"],
  esi: ["esic"],
  pt: ["professional tax"],
  lwf: ["labour welfare fund"],
  "payment-bank": ["bank account"],
  "payrun-cycle": ["pay date", "cut off"],
  "pay-slip": ["payslip", "salary slip"],
  "rounding-rules": ["round off"],
};

// Quick actions: open the page with its Add drawer already open. The page
// reads `?quick=<id>` (useQuickActionFromUrl) and runs its own Add menu
// item whose label is `actionLabel`, so the page's permission and
// configuration checks still apply. Shown before typing for the module on
// screen (up to 4).
export const QUICK_ACTIONS = [
  { id: "add-shift-earnings", title: "Add Shift Earnings", page: "/admin/payroll/pay-inputs/shift-earnings", module: "Payroll", keywords: ["shift allowance"] },
  { id: "add-overtime", title: "Add Overtime", page: "/admin/payroll/pay-inputs/overtime", module: "Payroll", keywords: ["ot"] },
  { id: "add-loan-advance", title: "Add Loan / Advance", page: "/admin/payroll/pay-inputs/loans-and-advances", module: "Payroll", keywords: ["loan", "advance"] },
  { id: "add-penalty", title: "Add Penalty", page: "/admin/payroll/pay-inputs/penalties", module: "Payroll", keywords: ["fine"] },
  { id: "add-leave-earnings", title: "Add Leave Earnings", page: "/admin/payroll/pay-inputs/leave", module: "Payroll", keywords: ["leave encashment"] },
  { id: "add-employee", title: "Add Employee", page: "/admin/hrzone/employees", module: "People", keywords: ["new employee", "onboard"] },
];

export const QUICK_ACTION_PARAM = "quick";
export const quickActionRoute = (action) => `${action.page}?${QUICK_ACTION_PARAM}=${action.id}`;
export const QUICK_ACTIONS_BEFORE_TYPING = 4;

const basePath = (path) => (path || "").split("?")[0];
const labelOf = (row) => row?.displayName || row?.name || "";

// The products the user can open, from the sidebar's routes.
export const searchableProducts = (routes) =>
  (routes || []).filter((route) => !route?.sectionLabel && !route?.hideInSidebar && route?.collapse);

// Pay Inputs switched off in Pay Input Configuration are left out, like the
// sidebar does (spec: pages behind a disabled feature flag are excluded).
const isConfigDisabled = (path, { domain, companyId }) => {
  const gated = CONFIG_GATED_FLYOUT_ITEMS.find((item) => item.path === path);
  return gated && domain && companyId
    ? getCachedPayInputConfig(domain, companyId, gated.payInputType)?.enabled === false
    : false;
};

// Builds the searchable list for the user. `accessByProduct` maps a product
// name to its provisioning payload; a product whose payload isn't loaded
// yet adds nothing, so nothing the user can't open is ever listed.
// `ignoreAccess` lists every sidebar page (used to record Recent only).
export const buildRegistry = (routes, accessByProduct, context, { ignoreAccess = false } = {}) => {
  const submenuAllowed = (productName, submenu, access) =>
    ignoreAccess || isSubmenuAccessible(productName, submenu, access);
  const rowAllowed = (submenu, nested, access) =>
    ignoreAccess || (isSectionRowAccessible(submenu, nested, access) && !isConfigDisabled(nested.path, context));
  const pages = [];
  const settings = [];
  const seen = new Set();
  const addPage = (entry) => {
    const key = `${entry.title}|${entry.route}`;
    if (seen.has(key)) return;
    seen.add(key);
    pages.push(entry);
  };
  const openablePaths = new Set();

  searchableProducts(routes).forEach((product) => {
    const access = accessByProduct[product.name];
    if (!access && !ignoreAccess) return;
    const productLabel = labelOf(product);
    (product.collapse || [])
      .filter((submenu) => !submenu?.hideInSidebar && submenuAllowed(product.name, submenu, access))
      .forEach((submenu) => {
        const children = (submenu.collapse || []).filter((nested) => !nested?.hideInSidebar);
        // Payroll Settings: its pages are listed as settings below.
        if (product.name === "Payroll" && submenu.sidebarFooter) {
          openablePaths.add("payroll-settings");
          return;
        }
        if (children.length === 0) {
          openablePaths.add(basePath(submenu.path));
          addPage({
            id: `page:${submenu.path}`,
            type: "page",
            title: labelOf(submenu),
            path: productLabel,
            route: submenu.path,
            module: product.name,
            keywords: PAGE_KEYWORDS[submenu.path] || [],
            row: submenu,
          });
          return;
        }
        children
          .filter((nested) => rowAllowed(submenu, nested, access))
          .forEach((nested) => {
            openablePaths.add(basePath(nested.path));
            addPage({
              id: `page:${nested.path}`,
              type: "page",
              title: labelOf(nested),
              path: `${productLabel} › ${labelOf(submenu)}`,
              route: nested.path,
              module: product.name,
              keywords: PAGE_KEYWORDS[nested.path] || PAGE_KEYWORDS[basePath(nested.path)] || [],
              row: nested,
            });
          });
      });
  });

  // Payroll Settings: only for users who can open it (spec: admins).
  if (openablePaths.has("payroll-settings")) {
    const setting = (item, path) => ({
      id: `setting:${item.key}:${settingsItemHref(item)}`,
      type: "setting",
      title: item.label,
      path,
      route: settingsItemHref(item),
      module: "Payroll",
      keywords: SETTING_KEYWORDS[item.key] || [],
      settingItem: item,
    });
    settings.push(setting({ ...PAYROLL_SETTINGS_OVERVIEW, label: "Payroll Settings" }, "Payroll"));
    PAYROLL_SETTINGS_MENU.forEach((group) =>
      group.items.forEach((item) => settings.push(setting(item, `Payroll Settings › ${group.label}`))),
    );
  }

  // A quick action needs its page to be openable.
  const actions = QUICK_ACTIONS.filter((action) => openablePaths.has(action.page)).map((action) => {
    const page = pages.find((entry) => basePath(entry.route) === action.page);
    return {
      id: `action:${action.id}`,
      type: "action",
      title: action.title,
      path: page ? `${page.path} › ${page.title}` : action.module,
      route: quickActionRoute(action),
      module: action.module,
      keywords: action.keywords || [],
    };
  });

  return { pages, settings, actions };
};

// ---- Matching and ranking (spec 4.4) ----

// Lower-case, punctuation turned into spaces, spaces collapsed.
export const normalize = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

// Where `query` sits in `title`, as [start, end] ranges for highlighting.
// Matches on the raw lower-cased title so the ranges line up with it.
const titleRanges = (title, query) => {
  const lower = String(title).toLowerCase();
  const raw = String(query).trim().toLowerCase().replace(/\s+/g, " ");
  if (!raw) return [];
  const at = lower.indexOf(raw);
  return at >= 0 ? [[at, at + raw.length]] : [];
};

// Rank of an entry for the query (lower is better); null when no match.
// 1 title starts with it, 2 a word in the title starts with it,
// 3 a keyword matches, 4 the title contains it.
export const rankEntry = (entry, query) => {
  const needle = normalize(query);
  if (!needle) return null;
  const title = normalize(entry.title);
  if (title.startsWith(needle)) return 1;
  if (title.split(" ").some((word, index, words) => words.slice(index).join(" ").startsWith(needle))) return 2;
  if ((entry.keywords || []).some((word) => {
    const keyword = normalize(word);
    return keyword.startsWith(needle) || keyword.split(" ").some((part) => part.startsWith(needle));
  })) return 3;
  if (title.includes(needle)) return 4;
  return null;
};

// Matches in one group, best first. Ties: recently opened first, then A-Z.
export const searchEntries = (entries, query, recentIds = []) =>
  entries
    .map((entry) => ({ entry, rank: rankEntry(entry, query) }))
    .filter((item) => item.rank != null)
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const recentA = recentIds.indexOf(a.entry.id);
      const recentB = recentIds.indexOf(b.entry.id);
      if (recentA !== recentB) {
        if (recentA === -1) return 1;
        if (recentB === -1) return -1;
        return recentA - recentB;
      }
      return a.entry.title.localeCompare(b.entry.title);
    })
    .map(({ entry }) => ({ ...entry, highlight: titleRanges(entry.title, query) }));

// The registry entry for a URL, so pages opened from the sidebar also go to
// Recent (spec 6.2: "a result (or any registry page)"). Query-string pages
// (Approvals ?view=leave, Payroll settings ?item=pf) match on the full URL.
export const findEntryForLocation = (registry, pathname, search) => {
  const full = `${pathname}${search || ""}`;
  const all = [...registry.settings, ...registry.pages];
  return (
    all.find((entry) => entry.route === full) ||
    all.find((entry) => !entry.route.includes("?") && entry.route === pathname) ||
    null
  );
};
